import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ApiError,
  type SourcingPostingBatch,
  type SourcingPostingItem,
} from 'api-clients/monolith';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  getPostingBatch,
  POSTING_BATCH_CHANNEL_PREFIX,
  POSTING_BATCH_EVENTS,
  retryPostingItem,
} from '@/api-clients/posting-batches';
import { useToaster } from '@/components/base/toaster';
import { queryKeys } from '@/query-keys';
import { useAuthContext } from '@/contexts/auth-context';
import { subscribeToPusherChannel } from '@/utils/pusher-client';

import {
  mapPostingBatch,
  postingBatchOpeningOrder,
} from './utils/map-posting-batch';

/** Pusher is best effort, so rows still move while any is running. */
export const POSTING_BATCH_REFETCH_MS = 30_000;

export const postingBatchKey = queryKeys.postingBatches.detail;

const isRunning = (item: SourcingPostingItem) =>
  item.state === 'queued' || item.state === 'posting';

/**
 * Keeps any cached row whose attempt is newer than the fetched one. A read
 * that started before a Retry, or before a colleague's Retry reached this
 * tab, answers for the older attempt and must not overwrite the row.
 */
export function fenceStaleItems(
  next: SourcingPostingBatch,
  cached: SourcingPostingBatch | undefined,
): SourcingPostingBatch {
  if (!cached) return next;
  const cachedById = new Map(cached.items.map(item => [item.id, item]));
  let fenced = false;
  const items = next.items.map(item => {
    const prev = cachedById.get(item.id);
    if (prev && prev.attempt > item.attempt) {
      fenced = true;
      return prev;
    }
    return item;
  });
  return fenced ? { ...next, items } : next;
}

const fetchPostingBatch = async (
  queryClient: QueryClient,
  postingBatchId: string,
  signal: AbortSignal,
) => {
  const request = getPostingBatch(postingBatchId);
  const cancel = () => request.cancel();
  signal.addEventListener('abort', cancel, { once: true });
  try {
    const batch = await request;
    return fenceStaleItems(
      batch,
      queryClient.getQueryData<SourcingPostingBatch>(
        postingBatchKey(postingBatchId),
      ),
    );
  } finally {
    signal.removeEventListener('abort', cancel);
  }
};

/**
 * The posting page's batch: one query entry, refreshed by Pusher events, on
 * every (re)subscription and every 30 s while a row runs. `result` groups the
 * rows into openings in the order of this mount's first successful read.
 */
export function usePostingBatch(postingBatchId: string) {
  const queryClient = useQueryClient();
  const { whoami } = useAuthContext();
  const queryKey = postingBatchKey(postingBatchId);

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      fetchPostingBatch(queryClient, postingBatchId, signal),
    staleTime: 0,
    refetchInterval: q =>
      q.state.data?.items.some(isRunning) ? POSTING_BATCH_REFETCH_MS : false,
    retry: (failures, error) =>
      failures < 1 && !(error instanceof ApiError && error.status < 500),
  });

  const [snapshot, setSnapshot] = useState<{
    id: string;
    order: string[];
  } | null>(null);
  const order = snapshot?.id === postingBatchId ? snapshot.order : null;
  if (!order && query.data) {
    setSnapshot({
      id: postingBatchId,
      order: postingBatchOpeningOrder(query.data),
    });
  }

  const channelName = query.data?.pusher_channel ?? '';
  const appKey = whoami?.recruiter_ui_env_vars?.REACT_APP_PUSHER_APP_KEY ?? '';
  const cluster = whoami?.recruiter_ui_env_vars?.REACT_APP_PUSHER_CLUSTER ?? '';
  useEffect(() => {
    if (!channelName.startsWith(POSTING_BATCH_CHANNEL_PREFIX)) return undefined;
    const refetch = () => {
      void queryClient.invalidateQueries({
        queryKey: postingBatchKey(postingBatchId),
        exact: true,
      });
    };
    // Pusher resubscribes after a reconnect, so this also covers events
    // missed while the socket was down.
    const handlers = Object.fromEntries(
      [...POSTING_BATCH_EVENTS, 'pusher:subscription_succeeded'].map(event => [
        event,
        refetch,
      ]),
    );
    return subscribeToPusherChannel({ appKey, cluster }, channelName, handlers);
  }, [queryClient, postingBatchId, channelName, appKey, cluster]);

  const result = useMemo(
    () =>
      query.data && order ? mapPostingBatch(query.data, order) : undefined,
    [query.data, order],
  );

  return { ...query, result };
}

/**
 * Retries one failed row. Waits for a first read still in flight, cancels any
 * other read so it cannot answer for the old attempt, then marks the row
 * queued on the next attempt and refetches. A 409 refetches and toasts.
 */
export function useRetryPostingItem(postingBatchId: string) {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { notifyError } = useToaster();
  const queryKey = postingBatchKey(postingBatchId);
  const pending = useRef(new Set<number>());
  const [pendingIds, setPendingIds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );

  const release = (itemId: number) => {
    pending.current.delete(itemId);
    setPendingIds(new Set(pending.current));
  };

  const { mutate } = useMutation({
    mutationFn: async (itemId: number) => {
      if (queryClient.getQueryData(queryKey)) {
        await queryClient.cancelQueries({ queryKey, exact: true });
      } else {
        await queryClient.ensureQueryData({
          queryKey,
          queryFn: ({ signal }) =>
            fetchPostingBatch(queryClient, postingBatchId, signal),
        });
      }
      const baseAttempt = queryClient
        .getQueryData<SourcingPostingBatch>(queryKey)
        ?.items.find(item => item.id === itemId)?.attempt;
      await retryPostingItem(itemId);
      return baseAttempt;
    },
    onSuccess: (baseAttempt, itemId) => {
      queryClient.setQueryData<SourcingPostingBatch>(queryKey, batch =>
        batch && baseAttempt !== undefined
          ? {
              ...batch,
              items: batch.items.map(item =>
                item.id === itemId && item.attempt === baseAttempt
                  ? {
                      ...item,
                      state: 'queued',
                      attempt: baseAttempt + 1,
                      reason_code: null,
                      reason_message: null,
                      retryable: false,
                      note: null,
                    }
                  : item,
              ),
            }
          : batch,
      );
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to create job posting',
          description: 'Error message for job posting creation',
        }),
      });
    },
    // A 409 means the row moved on (settled, retried elsewhere or unknown):
    // the refetch shows its current state.
    onSettled: (_data, _error, itemId) => {
      release(itemId);
      return queryClient.invalidateQueries({ queryKey, exact: true });
    },
  });

  const retry = useCallback(
    (itemId: number) => {
      if (pending.current.has(itemId)) return;
      pending.current.add(itemId);
      setPendingIds(new Set(pending.current));
      mutate(itemId);
    },
    [mutate],
  );

  return {
    retry,
    isRetrying: (itemId: number) => pendingIds.has(itemId),
  };
}
